import { elevateMfaSession } from "./elevateMfaSession";
import { promptMfaGate } from "./mfaGate";
import { decodeAccessToken, hasRecentMfaSession } from "./mfaSessionJwt";
import {
  isMfaSessionAbortError,
  MfaChallengeCancelledError,
  MfaEnrollmentRequiredError,
} from "./mfaSessionTypes";
import type { SessionAdapter } from "lib/session/SessionAdapter";

function sessionAdapter(): SessionAdapter {
  const adapter = window.sessionAdapter;
  if (!adapter) {
    throw new Error("Session adapter not found");
  }
  return adapter;
}

export type RequireMfaSessionOptions = {
  /** Shown in the MFA gate modal (enroll or challenge). */
  reason: string;
  /**
   * @deprecated Enrollment no longer redirects to Profile.
   * Kept for call-site compatibility; ignored.
   */
  returnPath?: string;
};

/**
 * Ensure the current session has a recent (≤30m) aal2 + totp amr.
 * Opens an in-context enroll wizard when no factor is enrolled.
 * Opens a TOTP challenge modal and elevates the session when needed.
 *
 * For MFA-gated screens, call this from an unlock CTA *before* enabling
 * editable fields (see docs/account-sensitive-settings.md Session MFA UX
 * standard). Do not rely on prompting only at save time.
 */
export async function requireMfaSession(options: RequireMfaSessionOptions): Promise<void> {
  const adapter = sessionAdapter();

  const enrolled = await adapter.hasTotpFactor();
  if (enrolled.error) throw enrolled.error;
  if (!enrolled.data?.enrolled) {
    try {
      await promptMfaGate({ mode: "enroll", reason: options.reason });
    } catch (error) {
      if (isMfaSessionAbortError(error)) throw error;
      throw new MfaEnrollmentRequiredError(
        error instanceof Error ? error.message : "Set up two-factor authentication to continue",
      );
    }
    return;
  }

  const session = await adapter.getSession();
  const jwt = decodeAccessToken(session?.access_token);
  if (hasRecentMfaSession(jwt)) {
    return;
  }

  let code: string;
  try {
    const result = await promptMfaGate({ mode: "challenge", reason: options.reason });
    if (result.mode !== "challenge") {
      throw new Error("Expected MFA challenge result");
    }
    code = result.code;
  } catch (error) {
    if (isMfaSessionAbortError(error)) throw error;
    throw new MfaChallengeCancelledError(
      error instanceof Error ? error.message : "Two-factor authentication cancelled",
    );
  }

  const digits = code.replace(/\D/g, "").slice(0, 6);
  if (digits.length !== 6) {
    throw new Error("Enter the 6-digit authenticator code");
  }

  await elevateMfaSession(digits);
}

export { isMfaSessionAbortError, MfaChallengeCancelledError, MfaEnrollmentRequiredError };
